"use client"

import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "./NominationRequestForm.css"
import { supabase } from "../lib/supabase"
import { formatLocalDate } from "../utils/timeSlots"
import { useScrollToError } from "../hooks/useScrollToError"

const formSchema = z.object({
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  dateOfBirth: z.date({ required_error: "Date of birth is required" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(10, { message: "Please enter a valid phone number" }),
  addressLine1: z.string().min(1, { message: "Address line 1 is required" }),
  addressLine2: z.string().optional(),
  city: z.string().min(1, { message: "City is required" }),
  postcode: z.string().min(5, { message: "Please enter a valid postcode" }),
  surgeryName: z.string().min(2, { message: "Surgery name is required" }),
  additionalNotes: z.string().optional(),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy",
  }),
})

function NominationRequestForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(null)

  // Scroll to top on success
  useEffect(() => {
    if (isSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [isSuccess])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      postcode: "",
      surgeryName: "",
      additionalNotes: "",
      consent: false,
    },
  })

  useScrollToError(errors, error)

  const dateOfBirth = watch("dateOfBirth")

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const submissionData = {
        first_name: data.firstName,
        last_name: data.lastName,
        date_of_birth: formatLocalDate(data.dateOfBirth),
        email: data.email,
        phone: data.phone,
        address_line_1: data.addressLine1,
        address_line_2: data.addressLine2 || null,
        city: data.city,
        postcode: data.postcode,
        surgery_name: data.surgeryName,
        additional_notes: data.additionalNotes || null,
        consent: data.consent,
      }

      const { error: supabaseError } = await supabase
        .from('nomination_requests')
        .insert([submissionData])

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      setIsSuccess(true)

      try {
        supabase.functions.invoke('send-notification-email', {
          body: { type: 'nomination_request', data: submissionData },
        })
      } catch (emailError) {
        console.error('Email notification failed:', emailError)
      }
    } catch (err) {
      console.error("Error submitting form:", err)
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="success-message">
        <div className="success-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3>Nomination Request Submitted Successfully</h3>
        <p>Thank you for nominating Goosnargh Pharmacy. We'll process your request and notify you once complete.</p>
        <button
          className="button"
          onClick={() => {
            setIsSuccess(false)
            reset()
          }}
        >
          Submit Another Nomination
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="nomination-form">
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            type="text"
            placeholder="John"
            {...register("firstName")}
            className={errors.firstName ? "error" : ""}
          />
          {errors.firstName && <p className="error-text">{errors.firstName.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            type="text"
            placeholder="Smith"
            {...register("lastName")}
            className={errors.lastName ? "error" : ""}
          />
          {errors.lastName && <p className="error-text">{errors.lastName.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="dateOfBirth">Date of Birth</label>
        <DatePicker
          selected={dateOfBirth}
          onChange={(date) => setValue("dateOfBirth", date)}
          maxDate={new Date()}
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          dropdownMode="select"
          dateFormat="dd/MM/yyyy"
          className={errors.dateOfBirth ? "error" : ""}
          placeholderText="Select date of birth"
        />
        {errors.dateOfBirth && <p className="error-text">{errors.dateOfBirth.message}</p>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            placeholder="john.smith@example.com"
            {...register("email")}
            className={errors.email ? "error" : ""}
          />
          {errors.email && <p className="error-text">{errors.email.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            type="tel"
            placeholder="07123 456789"
            {...register("phone")}
            className={errors.phone ? "error" : ""}
          />
          {errors.phone && <p className="error-text">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="addressLine1">Address Line 1</label>
        <input
          id="addressLine1"
          type="text"
          placeholder="123 Main Street"
          {...register("addressLine1")}
          className={errors.addressLine1 ? "error" : ""}
        />
        {errors.addressLine1 && <p className="error-text">{errors.addressLine1.message}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="addressLine2">Address Line 2 (optional)</label>
        <input
          id="addressLine2"
          type="text"
          placeholder="Flat 2, Building Name"
          {...register("addressLine2")}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            id="city"
            type="text"
            placeholder="Preston"
            {...register("city")}
            className={errors.city ? "error" : ""}
          />
          {errors.city && <p className="error-text">{errors.city.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="postcode">Postcode</label>
          <input
            id="postcode"
            type="text"
            placeholder="PR3 2AU"
            {...register("postcode")}
            className={errors.postcode ? "error" : ""}
          />
          {errors.postcode && <p className="error-text">{errors.postcode.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="surgeryName">GP Surgery Name</label>
        <input
          id="surgeryName"
          type="text"
          placeholder="e.g. Goosnargh Surgery"
          {...register("surgeryName")}
          className={errors.surgeryName ? "error" : ""}
        />
        {errors.surgeryName && <p className="error-text">{errors.surgeryName.message}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="additionalNotes">Additional Notes (optional)</label>
        <textarea
          id="additionalNotes"
          placeholder="Any additional information we should know"
          {...register("additionalNotes")}
        />
      </div>

      <div className="form-group consent-group">
        <div className="checkbox-container">
          <input type="checkbox" id="consent" {...register("consent")} className={errors.consent ? "error" : ""} />
          <label htmlFor="consent">
            I consent to Goosnargh Pharmacy processing my personal data for the purpose of registering as my nominated
            pharmacy
          </label>
        </div>
        <small>Your information will be handled in accordance with our privacy policy.</small>
        {errors.consent && <p className="error-text">{errors.consent.message}</p>}
      </div>

      <button type="submit" className="button" disabled={isSubmitting}>
        {isSubmitting ? "Processing..." : "Submit Nomination Request"}
      </button>
    </form>
  )
}

export default NominationRequestForm
